import { zodResolver } from "@hookform/resolvers/zod";
import { type CreateUserInput, CreateUserSchema } from "@schemas";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@veil/ui/components/button";
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from "@veil/ui/components/card";
import {
	Field,
	FieldError,
	FieldGroup,
	FieldLabel,
} from "@veil/ui/components/field";
import { Input } from "@veil/ui/components/input";
import {
	ResponsiveDialog,
	ResponsiveDialogAction,
	ResponsiveDialogCancel,
	ResponsiveDialogContent,
	ResponsiveDialogDescription,
	ResponsiveDialogFooter,
	ResponsiveDialogHeader,
	ResponsiveDialogTitle,
} from "@veil/ui/components/responsive-dialog";
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@veil/ui/components/select";
import { Skeleton } from "@veil/ui/components/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@veil/ui/components/table";
import { formatRelative } from "@veil/ui/lib/format";
import Loader2 from "lucide-react/dist/esm/icons/loader-2";
import Trash2 from "lucide-react/dist/esm/icons/trash-2";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { QueryError } from "@/components/query-state";
import { SshKeysCard } from "@/components/settings/ssh-keys-card";
import { api, unwrap } from "@/lib/api";
import { hasRole, type Role, useAuth } from "@/lib/auth-context";

interface UserRow {
	id: string;
	username: string;
	role: Role;
	createdAt: string;
}

export function Settings() {
	const { user: me } = useAuth();
	const queryClient = useQueryClient();

	const usersQuery = useQuery({
		queryKey: ["users"],
		queryFn: async () => {
			const data = await unwrap(api.api.users.get());
			return (data as { users: UserRow[] }).users;
		},
	});

	const form = useForm<CreateUserInput>({
		resolver: zodResolver(CreateUserSchema),
		defaultValues: { username: "", password: "", role: "operator" },
	});

	const create = useMutation({
		mutationFn: (values: CreateUserInput) => unwrap(api.api.users.post(values)),
		onSuccess: () => {
			toast.success("User created");
			form.reset({ username: "", password: "", role: "operator" });
			queryClient.invalidateQueries({ queryKey: ["users"] });
		},
		onError: (err) =>
			toast.error(err instanceof Error ? err.message : "Create failed"),
	});

	const updateRole = useMutation({
		mutationFn: ({ id, role }: { id: string; role: Role }) =>
			unwrap(api.api.users({ id }).role.patch({ role })),
		onSuccess: (_data, vars) => {
			// A silent success here reads as a no-op: the Select already shows
			// the new value whether or not the request landed.
			const changed = usersQuery.data?.find((u) => u.id === vars.id);
			toast.success(`${changed?.username ?? "User"} is now ${vars.role}`);
			queryClient.invalidateQueries({ queryKey: ["users"] });
		},
		onError: (err) =>
			toast.error(err instanceof Error ? err.message : "Update failed"),
	});

	const removeUser = useMutation({
		mutationFn: (id: string) => unwrap(api.api.users({ id }).delete()),
		onSuccess: (_data, id) => {
			const removed = usersQuery.data?.find((u) => u.id === id);
			toast.success(`Deleted ${removed?.username ?? "user"}`);
			queryClient.invalidateQueries({ queryKey: ["users"] });
		},
		onError: (err) =>
			toast.error(err instanceof Error ? err.message : "Delete failed"),
	});

	const onCreate = form.handleSubmit((values) => create.mutate(values));
	const role = form.watch("role");

	const [confirmDelete, setConfirmDelete] = useState<UserRow | null>(null);

	return (
		<div className="flex flex-col gap-6">
			<div>
				<h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
				<p className="text-sm text-muted-foreground">
					Users and the SSH keys this manager can reach worker hosts with.
				</p>
			</div>

			<Card>
				<CardHeader>
					<CardTitle className="text-base">Create user</CardTitle>
				</CardHeader>
				<CardContent>
					<form onSubmit={onCreate} noValidate>
						<FieldGroup>
							<div className="grid grid-cols-1 gap-4 md:grid-cols-4">
								<Field data-invalid={!!form.formState.errors.username}>
									<FieldLabel htmlFor="new-username">Username</FieldLabel>
									<Input
										id="new-username"
										aria-invalid={!!form.formState.errors.username}
										autoComplete="off"
										{...form.register("username")}
									/>
									<FieldError errors={[form.formState.errors.username]} />
								</Field>
								<Field data-invalid={!!form.formState.errors.password}>
									<FieldLabel htmlFor="new-password">Password</FieldLabel>
									<Input
										id="new-password"
										type="password"
										aria-invalid={!!form.formState.errors.password}
										autoComplete="new-password"
										{...form.register("password")}
									/>
									<FieldError errors={[form.formState.errors.password]} />
								</Field>
								<Field data-invalid={!!form.formState.errors.role}>
									<FieldLabel htmlFor="new-role">Role</FieldLabel>
									<Select
										value={role}
										onValueChange={(v) =>
											form.setValue("role", v as Role, { shouldDirty: true })
										}
									>
										<SelectTrigger id="new-role">
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectGroup>
												<SelectItem value="admin">admin</SelectItem>
												<SelectItem value="operator">operator</SelectItem>
												<SelectItem value="viewer">viewer</SelectItem>
											</SelectGroup>
										</SelectContent>
									</Select>
									<FieldError errors={[form.formState.errors.role]} />
								</Field>
								<div className="flex items-end">
									<Button type="submit" disabled={create.isPending}>
										{create.isPending && (
											<Loader2
												data-icon="inline-start"
												className="animate-spin"
											/>
										)}
										{create.isPending ? "Creating" : "Create"}
									</Button>
								</div>
							</div>
						</FieldGroup>
					</form>
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle className="text-base">Users</CardTitle>
				</CardHeader>
				<CardContent className="p-0">
					{usersQuery.isLoading ? <Skeleton className="m-6 h-32" /> : null}
					{!usersQuery.isLoading && usersQuery.isError ? (
						<div className="p-6">
							<QueryError
								title="Couldn't load users"
								error={usersQuery.error}
								onRetry={() => void usersQuery.refetch()}
								retrying={usersQuery.isFetching}
							/>
						</div>
					) : null}
					{!usersQuery.isLoading && !usersQuery.isError && (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Username</TableHead>
									<TableHead>Role</TableHead>
									<TableHead>Created</TableHead>
									<TableHead className="text-right" />
								</TableRow>
							</TableHeader>
							<TableBody>
								{(usersQuery.data ?? []).map((u) => (
									<TableRow key={u.id}>
										<TableCell className="font-mono font-medium">
											{u.username}
										</TableCell>
										<TableCell>
											<Select
												value={u.role}
												onValueChange={(v) =>
													updateRole.mutate({ id: u.id, role: v as Role })
												}
											>
												<SelectTrigger className="h-8 w-[120px]">
													<SelectValue />
												</SelectTrigger>
												<SelectContent>
													<SelectGroup>
														<SelectItem value="admin">admin</SelectItem>
														<SelectItem value="operator">operator</SelectItem>
														<SelectItem value="viewer">viewer</SelectItem>
													</SelectGroup>
												</SelectContent>
											</Select>
										</TableCell>
										<TableCell>{formatRelative(u.createdAt)}</TableCell>
										<TableCell className="text-right">
											<Button
												variant="ghost"
												size="icon"
												onClick={() => setConfirmDelete(u)}
												disabled={u.id === me?.id || removeUser.isPending}
												aria-label={`Delete ${u.username}`}
												title={
													u.id === me?.id
														? "You cannot delete yourself"
														: `Delete ${u.username}`
												}
											>
												<Trash2 />
											</Button>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					)}
				</CardContent>
			</Card>

			<SshKeysCard canEdit={hasRole(me, "operator")} />

			<ResponsiveDialog
				variant="alert"
				open={!!confirmDelete}
				onOpenChange={(open) => {
					if (!open) setConfirmDelete(null);
				}}
			>
				<ResponsiveDialogContent>
					<ResponsiveDialogHeader>
						<ResponsiveDialogTitle>
							Delete{" "}
							<span className="font-mono">{confirmDelete?.username}</span>?
						</ResponsiveDialogTitle>
						<ResponsiveDialogDescription>
							The account is removed immediately and any active session for it
							stops working. Operations this user started stay in the audit log.
							This cannot be undone.
						</ResponsiveDialogDescription>
					</ResponsiveDialogHeader>
					<ResponsiveDialogFooter>
						<ResponsiveDialogCancel disabled={removeUser.isPending}>
							Cancel
						</ResponsiveDialogCancel>
						<ResponsiveDialogAction
							variant="destructive"
							disabled={removeUser.isPending}
							onClick={(e) => {
								e.preventDefault();
								const target = confirmDelete;
								if (!target) return;
								removeUser.mutate(target.id, {
									onSettled: () => setConfirmDelete(null),
								});
							}}
						>
							{removeUser.isPending ? "Deleting…" : "Delete user"}
						</ResponsiveDialogAction>
					</ResponsiveDialogFooter>
				</ResponsiveDialogContent>
			</ResponsiveDialog>
		</div>
	);
}
